import { useEffect, useRef, useState } from 'react'
import { mediaSizeHint, type Media } from '@nhanho/shared'
import { media as mediaApi, explainError } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { Input } from '../ui/Input'

const ACCEPT = 'image/jpeg,image/png,image/webp'
/** Khớp `MAX_UPLOAD_MB` (mặc định 25) ở `apps/api/.env` và `client_max_body_size` nginx. */
const MAX_MB = 25
/** Khớp `upload.array('files', 20)` ở `apps/api/src/routes/media.ts`. */
const MAX_FILES = 20

/**
 * Tên file trông như máy ảnh/điện thoại tự đặt → không được dùng làm alt.
 * `IMG_1234`, `DSC0001`, `Screenshot 2026…`, `PXL_…`, `20260908_1230`, `photo_1`…
 */
const AUTO_NAME = /^(img|dsc|dcim|pxl|image|photo|picture|screenshot|screen shot|snapshot|untitled|z\d+|\d+)[\s_-]*\d*[\s_-]*\d*$/i

/**
 * Ngưỡng cảnh báo (KHÔNG chặn) cho ảnh PNG nặng.
 *
 * Vì sao có: đo 12/09 trên DB thật, biến thể `card` 600×600 dạng PNG nặng **887 KB** trong khi
 * cùng kích thước dạng JPEG chỉ **24–38 KB**. Ảnh render/AI và screenshot macOS mặc định ra
 * PNG, nên chúng vào đây rất nhiều: 8/11 ảnh trong thư viện là PNG và chiếm 90,5% dung lượng.
 *
 * `apps/api` từ 12/09 đã tự encode biến thể sang WebP nên khách **không** tải PNG — nhưng ảnh
 * gốc vẫn nằm nguyên trên Garage và server vẫn phải giải nén nó mỗi lần sinh ảnh (đo trên VPS:
 * 2,07 giây cho một file PNG 6,4 MB). Cảnh báo ở đây là để người đăng biết mà chọn định dạng.
 */
const PNG_WARN_BYTES = 1.5 * 1024 * 1024

/** Trần số điểm ảnh — khớp `MAX_PIXELS` ở `apps/api/src/lib/images.ts`. Server trả 413 nếu vượt. */
const MAX_PIXELS = 40_000_000

/** Đọc kích thước pixel mà không phải giải mã cả ảnh ra canvas. */
function readSize(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ w: img.naturalWidth, h: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      resolve(null)
      URL.revokeObjectURL(url)
    }
    img.src = url
  })
}

function cleanName(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function looksAuto(alt: string): boolean {
  return AUTO_NAME.test(alt.trim())
}

/** Alt hợp lệ: ≥ 3 ký tự và không phải tên máy ảnh. Cùng ngưỡng với server (422). */
export function altProblem(alt: string): string | null {
  const t = alt.trim()
  if (t.length < 3) return 'Ít nhất 3 ký tự'
  if (looksAuto(t)) return 'Đây là tên file máy đặt — hãy mô tả ảnh (vd: "Móc khoá cáo cam in 3D")'
  return null
}

interface Item {
  key: string
  file: File
  /** Kích thước pixel, đọc bất đồng bộ sau khi chọn file. `null` = chưa đọc xong / không đọc được. */
  dim: { w: number; h: number } | null
  alt: string
  /** Tên file người dùng đặt (chưa có đuôi). Trống = để server tự sinh như cũ. */
  name: string
  preview: string
  status: 'pending' | 'uploading' | 'done' | 'error'
  progress: number
  error?: string
  doc?: Media
}

/**
 * Kéo-thả / chọn nhiều ảnh → **bước đặt alt** (bắt buộc) → upload **tuần tự từng file**
 * bằng XHR để có tiến độ riêng và một ảnh hỏng không kéo cả lô.
 *
 * Alt bắt buộc trước khi upload vì đây là điểm SEO ảnh duy nhất mà chỉ người nhập biết —
 * để "sửa sau" thì không ai sửa, và Google đọc `IMG_1234`. Server cũng từ chối (422).
 * Không nhận SVG — API cũng từ chối, nhưng chặn ở đây để báo sớm.
 */
export function UploadDropzone({ onUploaded, compact }: { onUploaded: (docs: Media[]) => void; compact?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const open = items.length > 0

  // Giải phóng object URL (blob giữ RAM) khi unmount; khi đóng hộp thoại thì `clear()` lo.
  const previews = useRef(new Set<string>())
  useEffect(() => () => previews.current.forEach((u) => URL.revokeObjectURL(u)), [])
  const clear = () => {
    previews.current.forEach((u) => URL.revokeObjectURL(u))
    previews.current.clear()
    setItems([])
  }

  const pick = (list: FileList | File[]) => {
    if (busy) return
    const all = Array.from(list)
    const ok = all.filter((f) => ACCEPT.split(',').includes(f.type))
    const badType = all.filter((f) => !ACCEPT.split(',').includes(f.type))
    if (badType.length) toast.error(`Bỏ qua ${badType.map((f) => f.name).join(', ')}: chỉ nhận JPG, PNG, WebP`)
    const tooBig = ok.filter((f) => f.size > MAX_MB * 1024 * 1024)
    if (tooBig.length) toast.error(`Bỏ qua ${tooBig.map((f) => f.name).join(', ')}: vượt ${MAX_MB}MB`)
    let files = ok.filter((f) => f.size <= MAX_MB * 1024 * 1024)
    if (files.length > MAX_FILES) {
      toast.error(`Chỉ nhận ${MAX_FILES} ảnh mỗi lần — ${files.length - MAX_FILES} ảnh cuối bị bỏ`)
      files = files.slice(0, MAX_FILES)
    }
    if (inputRef.current) inputRef.current.value = ''
    if (!files.length) return
    const next: Item[] = files.map((file) => {
      const preview = URL.createObjectURL(file)
      previews.current.add(preview)
      return { key: crypto.randomUUID(), file, dim: null, alt: cleanName(file.name), name: cleanName(file.name), preview, status: 'pending', progress: 0 }
    })
    setItems(next)

    /**
     * Đọc kích thước pixel sau, không chặn việc hiện danh sách. Mỗi ảnh xong thì cập nhật
     * riêng nó — dùng `key` chứ không index, vì người dùng có thể đã xoá bớt ảnh trong lúc đợi.
     */
    for (const it of next) {
      void readSize(it.file).then((dim) => {
        if (dim) setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, dim } : x)))
      })
    }
  }

  const setAlt = (key: string, alt: string) => setItems((xs) => xs.map((i) => (i.key === key ? { ...i, alt } : i)))
  const setName = (key: string, name: string) => setItems((xs) => xs.map((i) => (i.key === key ? { ...i, name } : i)))
  const remove = (key: string) => setItems((xs) => xs.filter((i) => i.key !== key))
  const close = () => { if (!busy) clear() }

  const problems = items.filter((i) => i.status !== 'done').map((i) => altProblem(i.alt)).filter(Boolean)
  const pendingCount = items.filter((i) => i.status === 'pending' || i.status === 'error').length

  const upload = async () => {
    if (problems.length || !pendingCount) return
    setBusy(true)
    const done: Media[] = []
    let failed = 0
    for (const it of items) {
      if (it.status === 'done') continue
      setItems((xs) => xs.map((i) => (i.key === it.key ? { ...i, status: 'uploading', progress: 0, error: undefined } : i)))
      try {
        const doc = await mediaApi.uploadOne(it.file, it.alt.trim(), it.name.trim(), (r) =>
          setItems((xs) => xs.map((i) => (i.key === it.key ? { ...i, progress: r } : i))),
        )
        done.push(doc)
        setItems((xs) => xs.map((i) => (i.key === it.key ? { ...i, status: 'done', progress: 1, doc } : i)))
      } catch (e) {
        failed += 1
        const { message } = explainError(e, 'Upload thất bại')
        setItems((xs) => xs.map((i) => (i.key === it.key ? { ...i, status: 'error', error: message } : i)))
      }
    }
    setBusy(false)
    if (done.length) onUploaded(done)
    if (!failed) {
      toast.success(`Đã tải lên ${done.length} ảnh`)
      clear()
    } else {
      toast.error(`${done.length} ảnh xong, ${failed} ảnh lỗi — xem chi tiết trong hộp thoại`)
    }
  }

  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files) }}
        className={`rounded-card border-2 border-dashed text-center transition-colors ${compact ? 'p-4' : 'p-8'} ${
          dragging ? 'border-primary-700 bg-primary-50' : 'border-border-strong bg-white'
        }`}
      >
        <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => e.target.files && pick(e.target.files)} />
        <p className="text-sm text-ink">Kéo ảnh vào đây, hoặc</p>
        <Button variant="secondary" size="sm" className="mt-2" onClick={() => inputRef.current?.click()}>
          Chọn file
        </Button>
        <p className="mt-2 text-xs text-ink-muted">JPG · PNG · WebP · tối đa {MAX_MB}MB · {MAX_FILES} ảnh/lần. Xoá EXIF. {mediaSizeHint()}</p>
      </div>

      <Modal
        open={open}
        onClose={close}
        title={`Đặt alt text và tên file cho ${items.length} ảnh`}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>{items.every((i) => i.status === 'done') ? 'Đóng' : 'Huỷ'}</Button>
            <Button onClick={upload} loading={busy} disabled={!!problems.length || !pendingCount}>
              {pendingCount ? `Tải lên ${pendingCount} ảnh` : 'Đã xong'}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-xs text-ink-muted">
          Alt là mô tả ảnh cho Google và người dùng đọc màn hình. Viết như đang tả ảnh cho người không nhìn thấy: <em>"Móc khoá cáo màu cam in 3D trên nền gỗ"</em>. Tên file máy đặt (IMG_1234) không được chấp nhận.
        </p>
        <ul className="space-y-3">
          {items.map((it) => {
            const problem = it.status === 'done' ? null : altProblem(it.alt)
            return (
              <li key={it.key} className="flex items-start gap-3 rounded-card-sm border border-border p-2">
                <div className="relative size-16 shrink-0 overflow-hidden rounded-image bg-sand">
                  <img src={it.preview} alt="" className="size-full object-cover" />
                  {it.status === 'uploading' && (
                    <div className="absolute inset-x-0 bottom-0 h-1.5 bg-white/70"><div className="h-full bg-primary-700 transition-[width]" style={{ width: `${Math.round(it.progress * 100)}%` }} /></div>
                  )}
                  {it.status === 'done' && <span className="absolute inset-0 grid place-items-center bg-success/70 text-lg text-white">✓</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs text-ink-muted">
                    <span className="truncate">
                      {it.file.name} · {(it.file.size / 1024 / 1024).toFixed(1)} MB
                      {it.dim && ` · ${it.dim.w}×${it.dim.h}`}
                    </span>
                    {it.status === 'uploading' && <span>{Math.round(it.progress * 100)}%</span>}
                    {it.status === 'error' && <span className="text-danger">Lỗi</span>}
                  </div>

                  {/* Cảnh báo, KHÔNG chặn — người đăng biết rõ ảnh của mình hơn một ngưỡng cứng. */}
                  {it.file.type === 'image/png' && it.file.size > PNG_WARN_BYTES && (
                    <p className="mb-1 text-2xs leading-snug text-warning">
                      PNG {(it.file.size / 1024 / 1024).toFixed(1)} MB — nếu đây là ảnh chụp hoặc ảnh
                      render, lưu lại dạng JPEG hoặc WebP sẽ nhẹ hơn hàng chục lần mà mắt không
                      phân biệt được. PNG chỉ đáng dùng khi ảnh cần nền trong suốt.
                    </p>
                  )}
                  {it.dim && it.dim.w * it.dim.h > MAX_PIXELS && (
                    <p className="mb-1 text-2xs leading-snug text-danger">
                      {it.dim.w}×{it.dim.h} vượt trần {MAX_PIXELS / 1_000_000} triệu điểm ảnh — server
                      sẽ từ chối. Hãy thu nhỏ ảnh trước.
                    </p>
                  )}
                  {/*
                    Hai ô trông giống hệt nhau nếu không có nhãn — và ô tên file luôn được
                    điền sẵn nên placeholder của nó **không bao giờ hiện ra**. Nhãn nhỏ là
                    thứ duy nhất phân biệt được hai ô.
                  */}
                  <label className="mb-0.5 block text-2xs uppercase tracking-wider text-ink-faint">Alt text</label>
                  <Input
                    value={it.alt}
                    disabled={busy || it.status === 'done'}
                    aria-invalid={!!problem}
                    onChange={(e) => setAlt(it.key, e.target.value)}
                    placeholder="Mô tả ảnh…"
                  />
                  {problem && <div className="mt-1 text-xs text-danger">{problem}</div>}
                  {/*
                    Tên file lưu trên Garage. Bỏ trống thì server sinh như cũ. Trùng tên đã
                    có thì server nối `-<ngày-giờ>` — **không** ghi đè, vì ảnh được cache
                    một năm theo key (`storage.ts`).
                  */}
                  <label className="mb-0.5 mt-2 block text-2xs uppercase tracking-wider text-ink-faint">
                    Tên file <span className="normal-case tracking-normal">— để trống thì tự đặt; trùng tên cũ sẽ thêm ngày giờ</span>
                  </label>
                  <Input
                    value={it.name}
                    disabled={busy || it.status === 'done'}
                    onChange={(e) => setName(it.key, e.target.value)}
                    placeholder="tu-dat-ten…"
                    aria-label={`Tên file cho ${it.file.name}`}
                    className="font-mono text-xs"
                  />
                  {it.error && <div className="mt-1 text-xs text-danger">{it.error}</div>}
                </div>
                {it.status !== 'uploading' && it.status !== 'done' && (
                  <Button variant="ghost" size="sm" className="text-danger" onClick={() => remove(it.key)} aria-label="Bỏ ảnh này">✕</Button>
                )}
              </li>
            )
          })}
        </ul>
      </Modal>
    </>
  )
}
